import { useState, useEffect } from 'react';
import DeviceManager from './DeviceManager';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAdminUsers,
  createAdminUser,
  deleteAdminUser,
  resetAdminPassword,
  clearError,
} from '../../store/slices/adminUsersSlice';

const AdminUsers = () => {
  const dispatch = useDispatch();
  const { users, loading, error } = useSelector((state) => state.adminUsers);
  const { user: currentUser } = useSelector((state) => state.auth);
  
  const [showAddForm, setShowAddForm] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Form states
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    confirmPassword: '',
  });
  
  const [resetPassword, setResetPassword] = useState({
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    dispatch(fetchAdminUsers());
  }, [dispatch]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (newUser.password !== newUser.confirmPassword) {
      dispatch(clearError());
      // Set error manually since validation is client-side
      return;
    }
    
    const result = await dispatch(createAdminUser({
      username: newUser.username,
      password: newUser.password,
      confirmPassword: newUser.confirmPassword,
    }));
    
    if (result.type === 'adminUsers/createAdminUser/fulfilled') {
      setNewUser({ username: '', password: '', confirmPassword: '' });
      setShowAddForm(false);
      // Refresh the list
      dispatch(fetchAdminUsers());
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetPassword.newPassword !== resetPassword.confirmPassword) {
      return;
    }
    
    const result = await dispatch(resetAdminPassword({
      userId: selectedUser.id,
      newPassword: resetPassword.newPassword,
      confirmPassword: resetPassword.confirmPassword,
    }));
    
    if (result.type === 'adminUsers/resetAdminPassword/fulfilled') {
      setResetPassword({ newPassword: '', confirmPassword: '' });
      setShowResetModal(false);
      setSelectedUser(null);
    }
  };

  const handleDelete = async (userId) => {
    const result = await dispatch(deleteAdminUser(userId));
    if (result.type === 'adminUsers/deleteAdminUser/fulfilled') {
      setDeleteConfirm(null);
      // Refresh the list
      dispatch(fetchAdminUsers());
    }
  };

  const openResetModal = (user) => {
    setSelectedUser(user);
    setResetPassword({ newPassword: '', confirmPassword: '' });
    setShowResetModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
    <DeviceManager />
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Admin Users</h2>
        <button
          onClick={() => {
            setShowAddForm(!showAddForm);
            setNewUser({ username: '', password: '', confirmPassword: '' });
            dispatch(clearError());
          }}
          className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 transition-colors"
        >
          {showAddForm ? 'Cancel' : '+ Add Admin'}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700">
          {error}
        </div>
      )}

      {/* Add User Form */}
      {showAddForm && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Add New Admin User</h3>
          <form onSubmit={handleAddUser} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username
              </label>
              <input
                type="text"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2"
                placeholder="Enter username"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2"
                placeholder="Enter password (min 8 characters)"
                required
                minLength={8}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <input
                type="password"
                value={newUser.confirmPassword}
                onChange={(e) => setNewUser({ ...newUser, confirmPassword: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2"
                placeholder="Confirm password"
                required
                minLength={8}
              />
              {newUser.password && newUser.confirmPassword && newUser.password !== newUser.confirmPassword && (
                <p className="text-sm text-red-600 mt-1">Passwords do not match</p>
              )}
            </div>
            <button
              type="submit"
              disabled={loading || newUser.password !== newUser.confirmPassword}
              className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Admin User'}
            </button>
          </form>
        </div>
      )}

      {/* Users List */}
      <div className="space-y-3">
        {users.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No admin users found</p>
        ) : (
          users.map((user) => (
            <div
              key={user.id}
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h3 className="font-semibold text-gray-900">{user.username}</h3>
                  {user.id === currentUser?.id && (
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                      You
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Created: {formatDate(user.createdAt)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openResetModal(user)}
                  className="px-3 py-1.5 text-sm bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 transition-colors"
                >
                  Reset Password
                </button>
                {user.id !== currentUser?.id ? (
                  <button
                    onClick={() => setDeleteConfirm(user.id)}
                    className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                  >
                    Delete
                  </button>
                ) : (
                  <span className="px-3 py-1.5 text-sm text-gray-400 cursor-not-allowed">
                    Cannot delete yourself
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirm Delete</h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete this admin user? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              Reset Password for {selectedUser.username}
            </h3>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  value={resetPassword.newPassword}
                  onChange={(e) =>
                    setResetPassword({ ...resetPassword, newPassword: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="Enter new password (min 8 characters)"
                  required
                  minLength={8}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={resetPassword.confirmPassword}
                  onChange={(e) =>
                    setResetPassword({ ...resetPassword, confirmPassword: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="Confirm new password"
                  required
                  minLength={8}
                />
                {resetPassword.newPassword &&
                  resetPassword.confirmPassword &&
                  resetPassword.newPassword !== resetPassword.confirmPassword && (
                    <p className="text-sm text-red-600 mt-1">Passwords do not match</p>
                  )}
              </div>
              <div className="flex gap-3 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowResetModal(false);
                    setSelectedUser(null);
                    setResetPassword({ newPassword: '', confirmPassword: '' });
                    dispatch(clearError());
                  }}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    loading ||
                    resetPassword.newPassword !== resetPassword.confirmPassword ||
                    !resetPassword.newPassword
                  }
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default AdminUsers;

